<script setup>
/**
 * One text-channel starting point in the gallery grid — SMS or WhatsApp. Where a
 * live chat template previews the widget bubble it will render, a text one
 * previews its body as a chat bubble.
 *
 * The body means something different per channel: on SMS it is the message that
 * goes out, on WhatsApp it is suggested copy the agent takes into the form,
 * because the real send must carry a Meta-approved template.
 */
import { computed } from 'vue';

import Icon from 'dashboard/components-next/icon/Icon.vue';
import { TEMPLATE_CHANNELS } from 'dashboard/components-next/Campaigns/helpers/campaignGallery';

const props = defineProps({
  template: {
    type: Object,
    required: true,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
});

defineEmits(['select']);

const description = computed(() => props.template.content?.description ?? '');
const body = computed(() => props.template.content?.body ?? '');

const isWhatsapp = computed(
  () => props.template.channel === TEMPLATE_CHANNELS.WHATSAPP
);

const channelIcon = computed(() =>
  isWhatsapp.value ? 'i-ri-whatsapp-fill' : 'i-lucide-message-circle'
);

// Matches the channel identity used across the dashboard: WhatsApp teal, SMS
// slate — the same pairing as CAMPAIGN_CHANNEL_DOT_CLASSES.
const channelIconClass = computed(() =>
  isWhatsapp.value ? 'text-n-teal-9' : 'text-n-slate-9'
);
</script>

<template>
  <button
    type="button"
    :disabled="disabled"
    class="flex flex-col overflow-hidden text-left transition-shadow rounded-xl bg-n-solid-2 outline outline-1 -outline-offset-1 outline-n-container hover:shadow-md focus-visible:outline-2 focus-visible:outline-n-brand disabled:opacity-60 disabled:pointer-events-none"
    @click="$emit('select')"
  >
    <div class="w-full px-4 pt-4 pb-3 bg-n-alpha-1" aria-hidden="true">
      <div
        class="flex gap-2 p-3 shadow-sm rounded-xl rounded-bl-sm bg-n-solid-1 outline outline-1 -outline-offset-1 outline-n-container min-h-[4.5rem]"
      >
        <Icon
          :icon="channelIcon"
          :class="channelIconClass"
          class="flex-shrink-0 mt-0.5 size-4"
        />
        <p class="mb-0 text-xs text-n-slate-11 line-clamp-3">
          {{ body }}
        </p>
      </div>
    </div>
    <div class="flex flex-col gap-1 px-4 py-3">
      <span class="text-sm font-medium text-n-slate-12 line-clamp-1">
        {{ template.name }}
      </span>
      <span v-if="description" class="text-xs text-n-slate-11 line-clamp-2">
        {{ description }}
      </span>
    </div>
  </button>
</template>
